import { DOCS_URLS } from "@p0security/frontend-core";
import type { Auth0Components } from "@p0security/integrations-auth0-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-auth0-shared/hosting";
import type {
  Auth0ConnectorConfig,
  Auth0Integration,
  Auth0TenantConfig,
} from "@p0security/integrations-auth0-shared/types";
import {
  type FrontendInstallContext,
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import { p0InvokerEmail } from "@p0security/integrations-vaulted-credential-shared";
import {
  installedItem,
  installedItems,
} from "@p0security/shared/install/installed";
import {
  Alert,
  Collapse,
  CommandDisplay,
  Flex,
  Spacing,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import { compact, sortBy } from "lodash-es";
import type { ReactNode } from "react";

import { renderConnectorTerraform } from "./connector-iac.ts";
import {
  ApplicationInstructions,
  ConnectorHelp,
  type ReadGrant,
  TenantSteps,
} from "./steps.tsx";
import {
  type ReadGrantBlock,
  isVaultAccountBlock,
  renderReadGrantTerraform,
} from "./tenant-iac.ts";

const { Link, Paragraph } = Typography;

type Auth0Installer = FrontendInstaller<typeof Auth0Components>;
type InstallContext = FrontendInstallContext<Auth0Integration>;

/** Creates the provider that lists P0's installs of `integration`'s
 * iam-write component: the accounts, or projects, that a field placing a
 * connector or a secret can name. */
const newIamWriteInstallsProvider = (integration: string) =>
  otherInstalledIntegration({ component: "iam-write", integration });

/** The connectors a tenant can name: this integration's own installed
 * connectors, each with where it runs.
 *
 * This reads the installer's live config instead of fetching: the pointer
 * targets a sibling component, not another integration, so
 * `otherInstalledIntegration` would spend a read on a document the install
 * context already holds.
 */
const listConnectors = async ({ config }: InstallContext) =>
  sortBy(
    compact(
      Object.entries(config ? installedItems("connector", config) : {}).map(
        ([id, connector]) =>
          connector && {
            id,
            label: `${id}: ${renderHosting(connector.hosting)}`,
          }
      )
    ),
    "label"
  );

/** Whether a stored item has been set up: its page then shows the form, and
 * its Terraform only on request. */
const isSetUp = (item: { state?: string } | undefined) =>
  item?.state === "configure" || item?.state === "installed";

const DocsLink: React.FC = () => (
  <Link
    href={DOCS_URLS.AUTH0_INSTALL}
    rel="noopener noreferrer"
    target="_blank"
  >
    Auth0 install guide
  </Link>
);

/** Why a connector on Cloud Run can't be set up yet: its Terraform lets in
 * the service account P0 invokes it as, which is the one P0's Google Cloud
 * integration has. */
const MISSING_GOOGLE_CLOUD =
  "P0 invokes a connector on Cloud Run as the service account of its Google Cloud integration, which isn't installed. Install P0's Google Cloud IAM management for the connector's project first.";

const MissingGoogleCloud: React.FC = () => (
  <Alert message={MISSING_GOOGLE_CLOUD} showIcon type="error" />
);

const NewConnectorPreamble: React.FC = () => (
  <>
    <Paragraph>
      P0 grants access in your Auth0 tenants through a connector that you deploy
      in your own AWS account or Google Cloud project. Only the connector reads
      a tenant&apos;s client secret, and it acts only for the email domains you
      allow on its deployment.
    </Paragraph>
    <Paragraph>
      One connector can serve many tenants, so one is usually enough. Have ready
      P0&apos;s AWS IAM management or Google Cloud IAM management integration
      for the account or project the connector will run in. Where it runs
      can&apos;t change once the connector is added. See the <DocsLink />.
    </Paragraph>
  </>
);

const NewTenantPreamble: React.FC = () => (
  <>
    <Paragraph>
      Add each Auth0 tenant that P0 grants access in, by its canonical domain,
      such as acme.us.auth0.com: a custom domain can&apos;t be used. A tenant
      names the connector that acts on it, the client ID of a machine-to-machine
      application authorized for the Auth0 Management API, and the secret, in
      your own secret manager, that holds the application&apos;s client secret.
    </Paragraph>
    <Paragraph>
      Install a connector first. {VAULT_OUTSIDE_CONNECTOR_CLOUD} It can be in
      another account or project. Have ready P0&apos;s AWS Secrets Manager or
      Google Secret Manager integration for where the secret is. None of this
      can change once the tenant is added. See the <DocsLink />.
    </Paragraph>
    <Flex gap={8} vertical>
      <ApplicationInstructions />
      <Paragraph>
        Enter the application&apos;s client ID below. The next page shows how to
        store its client secret and let the connector read it.
      </Paragraph>
    </Flex>
  </>
);

/** What to do with one configuration of the tenant's read grant, shown
 * above it. `last` is whether "Next" follows it. */
const toGrantHeader = (block: ReadGrantBlock, { last }: { last: boolean }) => {
  const next = last ? <> Then click &quot;Next&quot;.</> : null;
  switch (block.purpose) {
    case "grant":
      return (
        <Paragraph>
          Add the following to your Terraform and apply it. It lets the
          connector read this tenant&apos;s client secret.{next}
        </Paragraph>
      );
    case "vault-account":
      return (
        <>
          <Paragraph>
            Then add the following where you manage AWS account{" "}
            {block.accountId}, which holds the secret, and apply it. Add its
            output to the policy of the secret&apos;s KMS key.{next}
          </Paragraph>
          <Spacing mb={12}>
            <Alert
              message="This replaces the secret's resource policy. If the secret already has one, add the statement to that policy instead."
              showIcon
              type="warning"
            />
          </Spacing>
        </>
      );
    default:
      throw assertNever(block);
  }
};

/** What a set-up tenant's page calls each configuration of its read
 * grant. */
const GRANT_REFERENCE_LABELS: Record<ReadGrantBlock["purpose"], string> = {
  grant: "Show the Terraform that lets the connector read the client secret",
  "vault-account": "Show the Terraform for the secret's account",
};

/** A set-up item's Terraform, collapsed, for an admin who needs it again. */
const TerraformReference: React.FC<{
  blocks: { key: string; label: string; terraform: string }[];
  children?: ReactNode;
}> = ({ blocks, children }) => (
  <Flex gap={8} vertical>
    {children}
    <Collapse
      items={blocks.map(({ key, label, terraform }) => ({
        children: <CommandDisplay commands={terraform} maxRows={20} />,
        key,
        label,
      }))}
    />
  </Flex>
);

/** Renders a tenant's read-grant Terraform from what the item and its
 * connector store, or says why it can't be. */
const toReadGrant = (
  context: InstallContext,
  tenant: string,
  item: Auth0TenantConfig
): ReadGrant => {
  const hosting = context.config
    ? installedItem("connector", context.config, item.connector)?.hosting
    : undefined;
  if (!hosting) return { status: "no-connector" };
  const blocks = renderReadGrantTerraform({
    connectorId: item.connector,
    hosting,
    tenant,
    item,
  });
  if (!blocks) return { hosting, status: "vault-outside-cloud" };
  return {
    blocks,
    hosting,
    status: "ready",
    vaultAccountId: blocks.find(isVaultAccountBlock)?.accountId,
  };
};

/** The configurations of a read grant, which has none until it's ready. */
const toBlocks = (grant: ReadGrant): ReadGrantBlock[] => {
  switch (grant.status) {
    case "no-connector":
    case "vault-outside-cloud":
      return [];
    case "ready":
      return grant.blocks;
    default:
      throw assertNever(grant);
  }
};

const renderConnectorInstructions = (
  context: InstallContext,
  connectorId: string,
  { hosting }: Auth0ConnectorConfig
) => renderConnectorTerraform({ connectorId, context, hosting });

/**
 * A connector comes first. Its new item's form takes its ID and where it
 * runs. Its page shows the Terraform that deploys it, above the "Next" that
 * checks P0 can invoke it, and "Finish" on the next page installs it.
 *
 * Each tenant then names a connector, the client id of its Management API
 * application and the secret that holds the client secret. Its page shows the
 * steps to create the application, store the secret and let the connector
 * read it, above the "Next" that validates the install through the
 * connector. "Finish" on the next page installs it.
 *
 * Once an item is set up, its page keeps its Terraform, collapsed.
 */
export const auth0Installer: Auth0Installer = {
  components: {
    connector: {
      instructions: (context, id, item) => {
        const terraform = renderConnectorInstructions(context, id, item);
        return terraform === undefined
          ? { help: <MissingGoogleCloud /> }
          : {
              commands: { iac: [{ command: terraform }] },
              help: (
                <ConnectorHelp
                  adminEmail={context.user?.email}
                  connectorId={id}
                  hosting={item.hosting}
                />
              ),
            };
      },
      items: {
        hosting: {
          labeler: () =>
            "Where the connector runs. Its Terraform deploys it there, and P0 invokes it there.",
          options: {
            aws: {
              items: {
                accountId: {
                  labeler: () =>
                    "The AWS account the connector runs in. P0 lists the accounts where its AWS IAM management is installed.",
                  optionProvider: newIamWriteInstallsProvider("aws"),
                },
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Lambda function. Its Terraform creates the function, and an IAM role of the same name that the connector runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The AWS region the connector runs in, such as us-west-2.",
                },
              },
            },
            gcp: {
              items: {
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Cloud Run service, in 6 to 30 characters. Its Terraform creates the service, and a service account of the same name that the connector runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The Google Cloud region the connector runs in, such as us-west1.",
                },
                projectId: {
                  labeler: () =>
                    "The Google Cloud project the connector runs in. P0 lists the projects where its Google Cloud IAM management is installed.",
                  optionProvider: newIamWriteInstallsProvider("gcloud"),
                },
              },
              prerequisiteMessages: async (context) =>
                p0InvokerEmail(context.installed)
                  ? undefined
                  : MISSING_GOOGLE_CLOUD,
            },
          },
        },
      },
      labeler: () =>
        "A name for this connector in P0, such as prod. Each tenant you add picks its connector by this name.",
      newPreamble: () => <NewConnectorPreamble />,
      // The framework passes the configure form's draft, so this reads the
      // item P0 stored.
      preamble: (context, id) => {
        const stored = context.config?.connector?.[id];
        if (!stored || !isSetUp(stored)) return undefined;
        const terraform = renderConnectorInstructions(context, id, stored);
        return terraform === undefined ? (
          <MissingGoogleCloud />
        ) : (
          <TerraformReference
            blocks={[
              {
                key: "terraform",
                label: "Show the connector's Terraform",
                terraform,
              },
            ]}
          />
        );
      },
    },
    tenant: {
      // The new item's form saved every field the Terraform needs, so render
      // it from what the item and its connector store.
      instructions: (context, tenant, item) => {
        const grant = toReadGrant(context, tenant, item);
        const blocks = toBlocks(grant);
        return {
          commands:
            blocks.length > 0
              ? {
                  iac: blocks.map((block, index) => ({
                    command: block.terraform,
                    header: toGrantHeader(block, {
                      last: index === blocks.length - 1,
                    }),
                  })),
                }
              : undefined,
          help: (
            <TenantSteps
              adminEmail={context.user?.email}
              grant={grant}
              item={item}
              tenant={tenant}
            />
          ),
        };
      },
      items: {
        connector: {
          optionProvider: listConnectors,
          prerequisiteMessages: async (context) =>
            (await listConnectors(context)).length === 0
              ? "Install an Auth0 connector first."
              : undefined,
        },
        vault: {
          labeler: () =>
            "Where the client secret is stored. It must be in the connector's cloud, and can be in another account or project.",
          options: {
            "aws-sm": {
              items: {
                install: {
                  labeler: () =>
                    "The AWS account the secret is in. P0 lists the accounts where its AWS Secrets Manager integration is installed.",
                  optionProvider: newIamWriteInstallsProvider("aws-sm"),
                },
                secretsRegion: {
                  labeler: () =>
                    "The AWS region the secret is in, such as us-west-2.",
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  labeler: () =>
                    "The Google Cloud project the secret is in. P0 lists the projects where its Google Secret Manager integration is installed.",
                  optionProvider: newIamWriteInstallsProvider("gcp-sm"),
                },
              },
            },
          },
        },
      },
      labeler: () =>
        "The tenant's canonical Auth0 domain, such as acme.us.auth0.com. A custom domain can't be used.",
      newPreamble: () => <NewTenantPreamble />,
      // Like the connector's preamble, this reads the stored item, which also
      // has the allowed domains the last validation read.
      preamble: (context, tenant) => {
        const stored = context.config?.tenant?.[tenant];
        if (!stored || !isSetUp(stored)) return undefined;
        const grant = toReadGrant(context, tenant, stored);
        return (
          <TerraformReference
            blocks={toBlocks(grant).map(({ purpose, terraform }) => ({
              key: purpose,
              label: GRANT_REFERENCE_LABELS[purpose],
              terraform,
            }))}
          >
            <TenantSteps
              adminEmail={context.user?.email}
              grant={grant}
              item={stored}
              tenant={tenant}
            />
          </TerraformReference>
        );
      },
    },
  },
};
